import type { ReactNode } from "react"
import { cn } from "@/shared/utils/general/cn"

/**
 * MobileTopBar — shared 44px mobile top navigation bar.
 *
 * Three composable slots: left, center, right — each accepts any ReactNode.
 * Handles safe areas (status bar), consistent height, and optional bottom
 * border. The SyncStatusProgressBar sits at the bottom edge via the
 * `relative` wrapper in the parent layout.
 *
 * 50px content area + status bar inset — matches v1 for a comfortable feel
 * with room for 17px titles and 22px icons.
 *
 * Used by:
 * - TopNavBar (main app navigation + selection mode)
 * - SettingsMobileTopBar (settings panel)
 * - Any future mobile top bars
 */

export interface MobileTopBarProps {
  /** Left slot — e.g. hamburger, back button, "Select All" */
  left?: ReactNode
  /** Center slot — e.g. title, icon + title, count */
  center?: ReactNode
  /** Right slot — e.g. menu, close, "Done" */
  right?: ReactNode
  /** Show bottom border. Default: true */
  bordered?: boolean
  /** Additional background classes or style overrides */
  transparent?: boolean
  /** Additional className on the outer element */
  className?: string
  /** Inline style overrides (for animated background etc.) */
  style?: React.CSSProperties
  /** data-* attributes for click-outside detection */
  "data-selection-toolbar"?: boolean
}

export function MobileTopBar({
  left,
  center,
  right,
  bordered = true,
  transparent = false,
  className,
  style,
  ...dataAttrs
}: MobileTopBarProps) {
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center px-3",
        bordered && "border-b border-border",
        !transparent && "bg-[var(--card)]",
        className,
      )}
      style={{
        height: "calc(var(--topbar-status-inset, var(--status-bar-height, env(safe-area-inset-top, 0px))) + 50px)",
        paddingTop: "var(--topbar-status-inset, var(--status-bar-height, env(safe-area-inset-top, 0px)))",
        ...style,
      }}
      {...dataAttrs}
    >
      {/* Left — z-10 above the absolutely-centered middle */}
      <div className="relative z-10 flex shrink-0 items-center">{left}</div>

      {/* Center — absolute inset so it's always perfectly centered,
          regardless of left/right content width */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ paddingTop: "var(--topbar-status-inset, var(--status-bar-height, env(safe-area-inset-top, 0px)))" }}>
        <div className="pointer-events-auto flex items-center">
          {center}
        </div>
      </div>

      {/* Right — z-10 + ml-auto to push to far right */}
      <div className="relative z-10 ml-auto flex shrink-0 items-center">{right}</div>
    </div>
  )
}
